import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";

const PrivacyPolicy = () => {
  const lastUpdated = new Date().toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });

  return (
    <div className="container mx-auto py-12 px-4 sm:px-6 lg:px-8 max-w-5xl">
      <div className="mb-8 text-center">
        <h1 className="text-4xl font-bold bg-gradient-to-r from-purple-600 via-blue-500 to-cyan-400 bg-clip-text text-transparent mb-4">
          Privacy Policy
        </h1>
        <p className="text-gray-500 text-lg max-w-3xl mx-auto">
          Last updated: {lastUpdated}
        </p>
        <Separator className="my-8" />
      </div>

      {/* Introduction */}
      <Card className="mb-8 shadow-lg border-0 bg-white/50 backdrop-blur-sm" id="introduction">
        <CardHeader>
          <CardTitle className="text-2xl font-bold text-gray-800">Introduction</CardTitle>
        </CardHeader>
        <CardContent className="text-gray-600 space-y-4">
          <p>
            Digika.ai (“<strong>Digika</strong>”, “<strong>we</strong>”, “<strong>us</strong>”, or “<strong>our</strong>”) is committed to protecting your personal data. This Privacy Policy explains what information we collect, how we use it, whom we share it with, and your rights under applicable data protection laws, including the EU/UK General Data Protection Regulation (GDPR).
          </p>
          <p>
            By using our platform and services (the “<strong>Services</strong>”), you acknowledge that you have read and understood this Policy. If you do not agree, please refrain from using the Services.
          </p>
        </CardContent>
      </Card>

      {/* Controller & Scope */}
      <Card className="mb-8 shadow-lg border-0 bg-white/50 backdrop-blur-sm" id="controller">
        <CardHeader>
          <CardTitle className="text-2xl font-bold text-gray-800">Data Controller & Scope</CardTitle>
        </CardHeader>
        <CardContent className="text-gray-600 space-y-4">
          <p>
            For the purposes of the GDPR, Digika is the <strong>data controller</strong> for personal data processed via our website(s) and platform. This Policy applies to visitors, <strong>freelancers</strong> and <strong>business clients</strong> using Digika.ai.
          </p>
          <p>
            Payments are processed by <strong>regulated payment service providers (PSPs)</strong> (e.g., PayPal Business, Binance Pay). Digika does not store your full payment card details and does not provide custody services.
          </p>
        </CardContent>
      </Card>

      {/* Information We Collect */}
      <Card className="mb-8 shadow-lg border-0 bg-white/50 backdrop-blur-sm" id="data-we-collect">
        <CardHeader>
          <CardTitle className="text-2xl font-bold text-gray-800">Information We Collect</CardTitle>
        </CardHeader>
        <CardContent className="text-gray-600 space-y-6">
          <div>
            <h3 className="text-xl font-semibold text-gray-700">Information You Provide</h3>
            <ul className="list-disc pl-6 space-y-2">
              <li>Account details (name, email, password), profile information, skills, experience, education, portfolio links.</li>
              <li>Business information (company name, role, billing/contact details).</li>
              <li>Content you upload or submit (CVs, proposals, messages, attachments).</li>
              <li>Preferences and communication choices (e.g., newsletter opt-in).</li>
            </ul>
          </div>

          <div>
            <h3 className="text-xl font-semibold text-gray-700">Automatically Collected</h3>
            <ul className="list-disc pl-6 space-y-2">
              <li>Log and device data (IP address, browser type/version, OS, language, timestamps).</li>
              <li>Usage data (pages viewed, clicks, referring/exit pages, session metrics).</li>
              <li>Cookies and similar technologies (see “Cookies & Tracking”).</li>
            </ul>
          </div>

          <div>
            <h3 className="text-xl font-semibold text-gray-700">From Third Parties</h3>
            <ul className="list-disc pl-6 space-y-2">
              <li>PSPs (payment status, limited identifiers—no full card data).</li>
              <li>Analytics and anti-fraud providers.</li>
              <li>Social/login providers (if you choose to connect an account).</li>
            </ul>
          </div>

          <div className="p-4 rounded-lg bg-blue-50 border border-blue-100">
            <strong>Note on blockchain data:</strong> If you use the optional DGK utility token in the future, certain transaction metadata may be published on public blockchains. Such records can be immutable and accessible by anyone who queries the network.
          </div>
        </CardContent>
      </Card>

      {/* Legal Bases */}
      <Card className="mb-8 shadow-lg border-0 bg-white/50 backdrop-blur-sm" id="legal-bases">
        <CardHeader>
          <CardTitle className="text-2xl font-bold text-gray-800">Legal Bases for Processing</CardTitle>
        </CardHeader>
        <CardContent className="text-gray-600 space-y-4">
          <ul className="list-disc pl-6 space-y-2">
            <li><strong>Contract</strong> – to create your account, provide core Services, and facilitate freelancer–client collaboration.</li>
            <li><strong>Legitimate interests</strong> – to secure the platform, prevent fraud/abuse, improve features, and support customer success.</li>
            <li><strong>Consent</strong> – for certain cookies/analytics/marketing where required; you can withdraw at any time.</li>
            <li><strong>Legal obligations</strong> – to comply with tax, accounting, and regulatory requirements.</li>
          </ul>
        </CardContent>
      </Card>

      {/* How We Use */}
      <Card className="mb-8 shadow-lg border-0 bg-white/50 backdrop-blur-sm" id="uses">
        <CardHeader>
          <CardTitle className="text-2xl font-bold text-gray-800">How We Use Your Information</CardTitle>
        </CardHeader>
        <CardContent className="text-gray-600 space-y-4">
          <ul className="list-disc pl-6 space-y-2">
            <li>Provide, operate, and improve the platform (profiles, search, matching, messaging).</li>
            <li>Process transactions with PSPs; manage payouts/settlements and invoices.</li>
            <li>Enable proposals, contracts, reviews, and reputation features.</li>
            <li>Provide support and communicate important updates or policy changes.</li>
            <li>Detect, prevent, and respond to security incidents and fraud.</li>
            <li>Comply with applicable laws and enforce our Terms.</li>
          </ul>
        </CardContent>
      </Card>

      {/* Sharing */}
      <Card className="mb-8 shadow-lg border-0 bg-white/50 backdrop-blur-sm" id="sharing">
        <CardHeader>
          <CardTitle className="text-2xl font-bold text-gray-800">Sharing Your Information</CardTitle>
        </CardHeader>
        <CardContent className="text-gray-600 space-y-4">
          <ul className="list-disc pl-6 space-y-2">
            <li><strong>Service providers and partners</strong> (hosting, analytics, PSPs, communications, security)—bound by contracts and confidentiality.</li>
            <li><strong>Business clients/freelancers</strong> – as necessary to enable proposals, collaboration, and reviews you initiate.</li>
            <li><strong>Business transfers</strong> – in connection with a merger, acquisition, or financing, subject to safeguards.</li>
            <li><strong>Legal and compliance</strong> – where required by law or to protect rights, users, or the platform.</li>
            <li><strong>With your consent</strong> – in cases where you explicitly agree to a specific disclosure.</li>
          </ul>
        </CardContent>
      </Card>

      {/* International Transfers */}
      <Card className="mb-8 shadow-lg border-0 bg-white/50 backdrop-blur-sm" id="transfers">
        <CardHeader>
          <CardTitle className="text-2xl font-bold text-gray-800">International Data Transfers</CardTitle>
        </CardHeader>
        <CardContent className="text-gray-600 space-y-4">
          <p>
            If personal data is transferred outside the EEA/UK, we implement appropriate safeguards (e.g., Standard Contractual Clauses, supplementary measures) to protect your information in accordance with GDPR requirements.
          </p>
        </CardContent>
      </Card>

      {/* Retention */}
      <Card className="mb-8 shadow-lg border-0 bg-white/50 backdrop-blur-sm" id="retention">
        <CardHeader>
          <CardTitle className="text-2xl font-bold text-gray-800">Data Retention</CardTitle>
        </CardHeader>
        <CardContent className="text-gray-600 space-y-4">
          <ul className="list-disc pl-6 space-y-2">
            <li><strong>Account & profiles</strong>: retained while your account is active. You may delete your account at any time from settings or by contacting us.</li>
            <li><strong>Talent pool / CV</strong>: if no update for <strong>12 months</strong>, we may deactivate visibility; we’ll send reminders (e.g., at 30 days) before removal from public search.</li>
            <li><strong>Support tickets</strong>: typically up to <strong>24 months</strong> after resolution.</li>
            <li><strong>Logs & security</strong>: typically up to <strong>12 months</strong>, unless needed longer for investigations.</li>
            <li><strong>Billing & tax records</strong>: retained per applicable law (e.g., up to <strong>10 years</strong> in some jurisdictions).</li>
          </ul>
        </CardContent>
      </Card>

      {/* Your Rights */}
      <Card className="mb-8 shadow-lg border-0 bg-white/50 backdrop-blur-sm" id="rights">
        <CardHeader>
          <CardTitle className="text-2xl font-bold text-gray-800">Your Privacy Rights</CardTitle>
        </CardHeader>
        <CardContent className="text-gray-600 space-y-4">
          <p>
            Depending on your location, you may have the following rights under the GDPR and similar laws:
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li>Access, rectification, and erasure of your personal data.</li>
            <li>Restriction of processing and objection to processing (including profiling).</li>
            <li>Data portability.</li>
            <li>Withdrawal of consent at any time (where processing is based on consent).</li>
            <li>Right to lodge a complaint with your supervisory authority.</li>
          </ul>
          <p className="mt-2">
            You can exercise your rights by contacting us (see “Contact Us”). We may need to verify your identity before processing your request.
          </p>
        </CardContent>
      </Card>

      {/* Security */}
      <Card className="mb-8 shadow-lg border-0 bg-white/50 backdrop-blur-sm" id="security">
        <CardHeader>
          <CardTitle className="text-2xl font-bold text-gray-800">Security Measures</CardTitle>
        </CardHeader>
        <CardContent className="text-gray-600 space-y-4">
          <p>
            We implement appropriate technical and organizational measures, including encryption in transit (TLS), secure development practices, access controls, monitoring, and regular assessments. While we strive to protect your data, no method of transmission or storage is 100% secure.
          </p>
        </CardContent>
      </Card>

      {/* Cookies */}
      <Card className="mb-8 shadow-lg border-0 bg-white/50 backdrop-blur-sm" id="cookies">
        <CardHeader>
          <CardTitle className="text-2xl font-bold text-gray-800">Cookies & Tracking</CardTitle>
        </CardHeader>
        <CardContent className="text-gray-600 space-y-4">
          <p>
            We use cookies and similar technologies to run the site, remember preferences, measure traffic, and improve the user experience. Where required, we obtain consent via our cookie banner and provide controls to manage categories (e.g., analytics, marketing).
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li><strong>Essential</strong>: required for core functionality and security.</li>
            <li><strong>Analytics</strong>: to understand usage and improve features.</li>
            <li><strong>Marketing</strong>: to deliver relevant content or communications (only with consent).</li>
          </ul>
          <p>
            You can change your cookie preferences at any time through the banner settings. For more details, see our Cookie Policy.
          </p>
        </CardContent>
      </Card>

      {/* Third-Party Links */}
      <Card className="mb-8 shadow-lg border-0 bg-white/50 backdrop-blur-sm" id="third-party">
        <CardHeader>
          <CardTitle className="text-2xl font-bold text-gray-800">Third-Party Websites</CardTitle>
        </CardHeader>
        <CardContent className="text-gray-600 space-y-4">
          <p>
            Our Services may contain links to third-party websites. We are not responsible for their privacy practices. We encourage you to review the privacy policies of any external sites you visit.
          </p>
        </CardContent>
      </Card>

      {/* Children */}
      <Card className="mb-8 shadow-lg border-0 bg-white/50 backdrop-blur-sm" id="children">
        <CardHeader>
          <CardTitle className="text-2xl font-bold text-gray-800">Children’s Privacy</CardTitle>
        </CardHeader>
        <CardContent className="text-gray-600 space-y-4">
          <p>
            Our Services are not directed to children under the age required by local law to consent to data processing. If we learn that we have collected personal data from a child without appropriate consent, we will take steps to delete such information.
          </p>
        </CardContent>
      </Card>

      {/* Changes */}
      <Card className="mb-8 shadow-lg border-0 bg-white/50 backdrop-blur-sm" id="changes">
        <CardHeader>
          <CardTitle className="text-2xl font-bold text-gray-800">Changes to This Policy</CardTitle>
        </CardHeader>
        <CardContent className="text-gray-600 space-y-4">
          <p>
            We may update this Privacy Policy from time to time. We will post the updated version here and adjust the “Last updated” date above. Material changes may also be communicated by email or in-app notice.
          </p>
        </CardContent>
      </Card>

      {/* Contact */}
      <Card className="mb-8 shadow-lg border-0 bg-white/50 backdrop-blur-sm" id="contact">
        <CardHeader>
          <CardTitle className="text-2xl font-bold text-gray-800">Contact Us</CardTitle>
        </CardHeader>
        <CardContent className="text-gray-600 space-y-4">
          <p>If you have questions or wish to exercise your privacy rights, please contact:</p>
          <div className="p-4 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg border border-blue-100 mt-2">
            <p><strong>Email (DPO/Privacy):</strong> <a className="text-blue-600 hover:underline" href="mailto:privacy@digika.ai">privacy@digika.ai</a></p>
            <p className="mt-2">For support enquiries, use <a className="text-blue-600 hover:underline" href="mailto:hello@digika.ai">hello@digika.ai</a> or visit the Support Center.</p>
          </div>
          <p className="text-sm text-gray-500">
            If you are an EEA/UK resident, you also have the right to lodge a complaint with your local supervisory authority.
          </p>
        </CardContent>
      </Card>

      <div className="text-center mt-12 text-sm text-gray-500">
        <p>© {new Date().getFullYear()} Digika.ai. All rights reserved.</p>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
